import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Ayuda contextual "?": en computador aparece al pasar el mouse; en el celular, al tocarla.
 * El globo es flotante (se pinta sobre toda la pantalla, no lo recorta ninguna tarjeta), con
 * colores fijos para que se lea igual en tema claro y oscuro.
 */
export function InfoTip({ text, title }: { text: string; title?: string; align?: 'left' | 'center' | 'right' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const b = btnRef.current.getBoundingClientRect();
    const w = Math.min(288, window.innerWidth - 16);
    const h = tipRef.current?.offsetHeight ?? 120;
    const left = Math.max(8, Math.min(b.left + b.width / 2 - w / 2, window.innerWidth - w - 8));
    const below = b.bottom + 8 + h <= window.innerHeight - 8;
    setPos({ left, top: below ? b.bottom + 8 : Math.max(8, b.top - 8 - h) });
  }, [open, text]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <span className="inline-flex align-middle" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        ref={btnRef}
        type="button"
        aria-label={title ? `Ayuda: ${title}` : 'Ayuda'}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onBlur={() => setOpen(false)}
        className="flex h-5 w-5 items-center justify-center rounded-full border border-ink-300 text-[11px] font-bold leading-none text-ink-500 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
      >
        ?
      </button>
      {open &&
        createPortal(
          <span
            ref={tipRef}
            role="tooltip"
            style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, width: Math.min(288, window.innerWidth - 16) }}
            className="pointer-events-none fixed z-[70] rounded-xl border border-white/10 bg-[#0f172a] p-3 text-left text-xs font-normal leading-relaxed text-white shadow-float"
          >
            {title && <strong className="mb-1 block text-[12px] text-white">{title}</strong>}
            <span className="text-slate-100">{text}</span>
          </span>,
          document.body
        )}
    </span>
  );
}
